import { useMemo } from "react";
import {
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import { fontFamily, radius, space, textStyle } from "@passfirst/ui";

import { type Colors, useColors } from "../../theme";
import { REPORT_REASONS, type Question, type ReportReason } from "../types";

interface Props {
  current: Question | null;
  open: boolean;
  setOpen: (v: boolean) => void;
  reason: ReportReason | null;
  setReason: (r: ReportReason | null) => void;
  comment: string;
  setComment: (v: string) => void;
  submitting: boolean;
  sent: boolean;
  /** Last submit failed — shows an inline "tap to retry" affordance. */
  failed?: boolean;
  onSubmit: (current: Question | null) => Promise<void>;
}

// Owns the three states of the report-this-question affordance:
//   sent    → "Thanks — we'll review this question."
//   open    → inline panel with reason picker + comment + Cancel/Send
//   default → subtle "Report this question" trigger link
//
// Admins triage the queue out-of-band; this stays a quiet quality-
// signal channel rather than a primary CTA.
export function ReportSection(props: Props): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const {
    current,
    open,
    setOpen,
    reason,
    setReason,
    comment,
    setComment,
    submitting,
    sent,
    failed,
    onSubmit,
  } = props;

  if (sent) {
    return (
      <Text style={styles.reportSent}>
        Thanks — we&apos;ll review this question.
      </Text>
    );
  }

  if (!open) {
    return (
      <Pressable
        onPress={() => setOpen(true)}
        style={styles.reportTrigger}
      >
        <Text style={styles.reportTriggerText}>Report this question</Text>
      </Pressable>
    );
  }

  return (
    <View style={styles.reportPanel}>
      <Text style={styles.reportPrompt}>What&apos;s wrong with this question?</Text>
      {REPORT_REASONS.map((r) => (
        <Pressable
          key={r.value}
          onPress={() => setReason(r.value)}
          style={[
            styles.reportReasonRow,
            reason === r.value && styles.reportReasonRowPicked,
          ]}
        >
          <Text style={styles.reportReasonText}>{r.label}</Text>
        </Pressable>
      ))}
      <TextInput
        style={styles.reportComment}
        placeholder="Optional: more detail (≤500 chars)"
        placeholderTextColor={colors.textMuted}
        value={comment}
        onChangeText={setComment}
        multiline
        maxLength={500}
      />
      <View style={styles.reportActions}>
        <Pressable
          onPress={() => {
            setOpen(false);
            setReason(null);
            setComment("");
          }}
          style={styles.reportCancel}
        >
          <Text style={styles.reportCancelText}>Cancel</Text>
        </Pressable>
        <Pressable
          onPress={() => void onSubmit(current)}
          disabled={!reason || submitting}
          style={[
            styles.reportSubmit,
            (!reason || submitting) && styles.reportSubmitDisabled,
          ]}
        >
          <Text style={styles.reportSubmitText}>
            {submitting
              ? "Sending…"
              : failed
                ? "Couldn't send — tap to retry"
                : "Send report"}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    reportTrigger: {
      marginTop: space[2],
      alignSelf: "flex-start",
    },
    reportTriggerText: {
      ...textStyle.bodySmall,
      color: colors.textMuted,
      textDecorationLine: "underline",
    },
    reportSent: {
      ...textStyle.bodySmall,
      color: colors.textCorrect,
      marginTop: space[2],
    },
    reportPanel: {
      marginTop: space[3],
      paddingTop: space[3],
      borderTopWidth: 1,
      borderTopColor: colors.border,
      gap: space[2],
    },
    reportPrompt: {
      ...textStyle.label,
      color: colors.textPrimary,
    },
    reportReasonRow: {
      paddingVertical: space[2],
      paddingHorizontal: space[3],
      borderRadius: radius.sm,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    reportReasonRowPicked: {
      borderColor: colors.borderPicked,
      backgroundColor: colors.surfacePicked,
    },
    reportReasonText: { ...textStyle.bodySmall, color: colors.textPrimary },
    reportComment: {
      ...textStyle.bodySmall,
      color: colors.textPrimary,
      backgroundColor: colors.surface,
      borderColor: colors.border,
      borderWidth: 1,
      borderRadius: radius.sm,
      padding: space[3],
      minHeight: 60,
      fontFamily: fontFamily.regular,
    },
    reportActions: {
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: space[2],
    },
    reportCancel: {
      paddingVertical: space[2],
      paddingHorizontal: space[3],
    },
    reportCancelText: { ...textStyle.bodySmall, color: colors.textMuted },
    reportSubmit: {
      backgroundColor: colors.brand,
      paddingVertical: space[2],
      paddingHorizontal: space[4],
      borderRadius: radius.sm,
    },
    reportSubmitDisabled: { opacity: 0.4 },
    reportSubmitText: {
      ...textStyle.bodySmall,
      color: colors.textInverse,
      fontFamily: fontFamily.semiBold,
    },
  });
}
